import { useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Code2, Play, Loader2, Shield, Copy, FileCode, Wand2, ArrowRight, CheckCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const languages = [
  { id: 'python', label: 'Python' }, { id: 'javascript', label: 'JavaScript' },
  { id: 'typescript', label: 'TypeScript' }, { id: 'java', label: 'Java' },
  { id: 'go', label: 'Go' }, { id: 'rust', label: 'Rust' },
  { id: 'php', label: 'PHP' }, { id: 'ruby', label: 'Ruby' },
  { id: 'csharp', label: 'C#' }, { id: 'sql', label: 'SQL' },
];

const sampleCode = `import sqlite3

def get_user(username):
    conn = sqlite3.connect('users.db')
    cursor = conn.cursor()
    # Vulnerable to SQL Injection
    query = f"SELECT * FROM users WHERE username = '{username}'"
    cursor.execute(query)
    return cursor.fetchone()

def login(request):
    password = request.form['password']
    # Storing password in plain text
    db.execute("INSERT INTO users (password) VALUES (?)", (password,))
    
    # Hardcoded secret key
    SECRET_KEY = "super_secret_key_123"
    token = jwt.encode({"user": username}, SECRET_KEY)
    
    # XSS vulnerability - unescaped output
    return f"<h1>Welcome {request.args.get('name')}</h1>"`;

export default function CodeScannerPage() {
  const [code, setCode] = useState('');
  const [language, setLanguage] = useState('python');
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState(null);
  const [remediating, setRemediating] = useState(false);
  const [fixedCode, setFixedCode] = useState(null);
  const [activeTab, setActiveTab] = useState('results');

  const handleScan = async () => {
    if (!code.trim()) return;
    setScanning(true);
    setResult(null);
    setFixedCode(null);
    setActiveTab('results');
    try {
      const res = await axios.post(`${API}/ai/scan-code`, { code, language }, { withCredentials: true });
      setResult(res.data.analysis);
      toast.success('Code analysis complete');
    } catch {
      setResult('Error: Failed to analyze code.');
      toast.error('Analysis failed');
    } finally { setScanning(false); }
  };

  const handleAutoRemediate = async () => {
    if (!code.trim() || !result) return;
    setRemediating(true);
    setFixedCode(null);
    try {
      const res = await axios.post(`${API}/ai/auto-remediate`, { code, language, findings: typeof result === 'string' ? result : JSON.stringify(result) }, { withCredentials: true });
      setFixedCode(res.data.remediated_code);
      setActiveTab('fixed');
      toast.success('Auto-remediation complete');
    } catch {
      toast.error('Auto-remediation failed');
    } finally { setRemediating(false); }
  };

  const applyFix = () => {
    if (fixedCode) {
      // Clean markdown code blocks if present
      let clean = fixedCode;
      if (clean.startsWith('```')) {
        clean = clean.replace(/^```\w*\n?/, '').replace(/\n?```$/, '');
      }
      setCode(clean);
      setFixedCode(null);
      setResult(null);
      setActiveTab('results');
      toast.success('Fixed code applied to editor');
    }
  };

  const loadSample = () => { setCode(sampleCode); setLanguage('python'); setResult(null); setFixedCode(null); };

  return (
    <div className="space-y-6" data-testid="code-scanner-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold font-heading tracking-tight" data-testid="code-scanner-title">Source Code Scanner</h1>
          <p className="text-sm text-muted-foreground mt-1">AI-powered security analysis with auto-remediation</p>
        </div>
        <Button variant="outline" className="gap-2" onClick={loadSample} data-testid="load-sample-btn"><FileCode className="h-4 w-4" /> Load Sample</Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Code Input */}
        <Card data-testid="code-input-card">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base font-heading flex items-center gap-2"><Code2 className="h-4 w-4 text-primary" /> Source Code</CardTitle>
              <Select value={language} onValueChange={setLanguage}>
                <SelectTrigger className="w-[140px] h-8" data-testid="language-select"><SelectValue /></SelectTrigger>
                <SelectContent>{languages.map(l => <SelectItem key={l.id} value={l.id}>{l.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent>
            <Textarea value={code} onChange={e => setCode(e.target.value)} placeholder="Paste your source code here for security analysis..."
              className="min-h-[400px] font-mono text-sm bg-[#0d1117] text-[#c9d1d9] border-border resize-none" data-testid="code-input" />
            <div className="flex items-center justify-between mt-4">
              <span className="text-xs text-muted-foreground">{code.split('\n').length} lines</span>
              <div className="flex gap-2">
                <Button onClick={handleScan} disabled={scanning || !code.trim()} className="gap-2" data-testid="scan-code-btn">
                  {scanning ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
                  {scanning ? 'Analyzing...' : 'Scan Code'}
                </Button>
                <Button onClick={handleAutoRemediate} disabled={remediating || !code.trim() || !result} variant="outline" className="gap-2" data-testid="auto-remediate-btn">
                  {remediating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />}
                  {remediating ? 'Fixing...' : 'Auto-Fix'}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Results */}
        <Card data-testid="scan-results-card">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base font-heading flex items-center gap-2"><Shield className="h-4 w-4 text-primary" /> Results</CardTitle>
              {fixedCode && <Badge variant="outline" className="border-green-500/30 text-green-500 text-xs gap-1"><CheckCircle className="h-3 w-3" /> Fix Available</Badge>}
            </div>
          </CardHeader>
          <CardContent>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="mb-3" data-testid="results-tabs">
                <TabsTrigger value="results">Analysis</TabsTrigger>
                <TabsTrigger value="fixed" disabled={!fixedCode}>Fixed Code</TabsTrigger>
              </TabsList>

              <TabsContent value="results">
                <ScrollArea className="h-[420px]">
                  {scanning && (
                    <div className="flex items-center justify-center h-[380px]">
                      <div className="text-center"><Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-3" /><p className="text-sm text-muted-foreground">AI is analyzing your code...</p></div>
                    </div>
                  )}
                  {!scanning && !result && (
                    <div className="flex items-center justify-center h-[380px]">
                      <div className="text-center"><Code2 className="h-10 w-10 text-muted-foreground/30 mx-auto mb-3" /><p className="text-sm text-muted-foreground">Paste code and click "Scan Code"</p></div>
                    </div>
                  )}
                  {!scanning && result && (
                    <pre className="whitespace-pre-wrap text-sm font-mono leading-relaxed text-foreground bg-muted/30 p-4 rounded-lg" data-testid="scan-result-output">
                      {typeof result === 'string' ? result : JSON.stringify(result, null, 2)}
                    </pre>
                  )}
                </ScrollArea>
              </TabsContent>

              <TabsContent value="fixed">
                <ScrollArea className="h-[380px]">
                  {fixedCode && (
                    <pre className="whitespace-pre-wrap text-sm font-mono leading-relaxed text-[#c9d1d9] bg-[#0d1117] p-4 rounded-lg" data-testid="fixed-code-output">
                      {fixedCode}
                    </pre>
                  )}
                </ScrollArea>
                {fixedCode && (
                  <div className="flex gap-2 mt-3">
                    <Button onClick={applyFix} className="gap-2 flex-1" data-testid="apply-fix-btn">
                      <ArrowRight className="h-4 w-4" /> Apply Fix to Editor
                    </Button>
                    <Button variant="outline" onClick={() => { navigator.clipboard.writeText(fixedCode); toast.success('Copied'); }} data-testid="copy-fixed-btn">
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
